import type Keycloak from 'keycloak-js';

export const keycloakConfig = {
  url: process.env.NEXT_PUBLIC_AUTH_SERVER_URL as string,
  realm: process.env.NEXT_PUBLIC_AUTH_SERVER_REALM as string,
  clientId: process.env.NEXT_PUBLIC_AUTH_SERVER_CLIENT_ID as string,
};

let keycloak: Keycloak | undefined;
let keycloakModulePromise: Promise<typeof import('keycloak-js')> | null = null;
let keycloakInstancePromise: Promise<Keycloak> | null = null;

const loadKeycloakModule = () => {
  if (!keycloakModulePromise) {
    keycloakModulePromise = import('keycloak-js');
  }
  return keycloakModulePromise;
};

/**
 * Deduped on a module-level promise: `initKeycloak()` (idle callback) and
 * `getToken()` (request interceptor) race on first load, and a plain
 * `if (keycloak)` check would let both pass the guard while the dynamic import
 * is in flight — constructing two instances, only one of which gets `init()`ed.
 */
export const getKeycloakInstance = async (): Promise<Keycloak | undefined> => {
  if (typeof window === 'undefined') return undefined;
  if (keycloak) return keycloak;

  if (!keycloakInstancePromise) {
    keycloakInstancePromise = loadKeycloakModule()
      .then(({ default: KeycloakCtor }) => {
        keycloak = new KeycloakCtor(keycloakConfig);
        return keycloak;
      })
      .catch(error => {
        keycloakModulePromise = null;
        keycloakInstancePromise = null;
        throw error;
      });
  }

  return keycloakInstancePromise;
};

/** Sync accessor — undefined until `getKeycloakInstance()` / `initKeycloak()` runs. */
export const getKeycloakSync = (): Keycloak | undefined => keycloak;

let initPromise: Promise<boolean> | null = null;
let initResolved = false;
let lastInitFailureAt = 0;

/**
 * `keycloak.init()` is not guaranteed to settle. Its `check-sso` path awaits
 * `postMessage` replies from hidden iframes (the login-status iframe and the
 * silent-check-sso iframe) with no timeout of its own, so anything that stops
 * those iframes from reporting back — a silent-check-sso URL that is not a Valid
 * Redirect URI on the client, one that does not render `src/app/silent-check-sso`,
 * a blocked or unreachable IDP origin — leaves the promise pending forever. Sits
 * above keycloak's own 10s
 * `messageReceiveTimeout` so we only pre-empt the genuinely unbounded waits.
 */
const INIT_TIMEOUT_MS = 12_000;

/** Keeps a failing `init()` from being retried by every outgoing request. */
const INIT_RETRY_COOLDOWN_MS = 30_000;

/** True after `initKeycloak()` has finished (success). Survives React remounts. */
export const isKeycloakInitialized = () => initResolved;

/** True once `initKeycloak()` has been called, including while the promise is in flight. */
export const hasKeycloakInitStarted = () => initPromise !== null;

const withTimeout = <T>(promise: Promise<T>, ms: number, message: string): Promise<T> =>
  new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error(message)), ms);
    promise.then(
      value => {
        clearTimeout(timer);
        resolve(value);
      },
      error => {
        clearTimeout(timer);
        reject(error);
      },
    );
  });

export const initKeycloak = async (): Promise<boolean> => {
  if (initPromise) return initPromise;
  if (Date.now() - lastInitFailureAt < INIT_RETRY_COOLDOWN_MS) return false;

  const instance = await getKeycloakInstance();
  if (!instance) return false;
  if (initPromise) return initPromise;

  // `getKeycloakInstance()` already returned, so we are in the browser.
  // Trailing slash, no `.html`: `trailingSlash: true` rewrites document
  // navigations to `/silent-check-sso.html` into this path anyway, and the
  // rewritten URL is what the iframe actually lands on. Pointing straight at it
  // keeps the redirect target and the route that serves it in agreement.
  const silentCheckSsoRedirectUri = `${window.location.origin}/silent-check-sso/`;

  initPromise = withTimeout(
    instance.init({
      onLoad: 'check-sso',
      flow: 'standard',
      pkceMethod: 'S256',
      silentCheckSsoRedirectUri,
    }),
    INIT_TIMEOUT_MS,
    `Timed out after ${INIT_TIMEOUT_MS}ms waiting for keycloak.init() — the SSO check iframe never reported back. Verify "${silentCheckSsoRedirectUri}" is a Valid Redirect URI on the "${keycloakConfig.clientId}" client.`,
  )
    .then(authenticated => {
      initResolved = true;
      return authenticated;
    })
    .catch(err => {
      // A Keycloak instance is single-use (`didInitialize`): calling `init()`
      // on it again throws immediately, so a retry has to start from a fresh
      // instance. Dropping it also makes `getKeycloakSync()` report null rather
      // than a half-initialized instance.
      keycloak = undefined;
      keycloakInstancePromise = null;
      initPromise = null;
      initResolved = false;
      lastInitFailureAt = Date.now();
      throw err;
    });

  return initPromise;
};

export const getToken = async (): Promise<string | null> => {
  try {
    await initKeycloak();
  } catch {
    return null;
  }

  // Read the instance *after* init: a failed attempt replaces it, so a
  // reference taken beforehand can point at a discarded one.
  const instance = getKeycloakSync();
  if (!instance?.authenticated) return null;

  if (instance.isTokenExpired()) {
    try {
      await instance.updateToken(30);
    } catch (error) {
      console.error('Failed to refresh the token', error);
      return null;
    }
  }

  return instance.token ?? null;
};
